import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';

function VocabularyManager() {
  const [knownWords, setKnownWords] = useState([]);
  const [learningWords, setLearningWords] = useState([]);
  const [activeTab, setActiveTab] = useState('known');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newWord, setNewWord] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not logged in
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Fetch vocabulary lists
    fetchVocabulary();
  }, [user, navigate]);

  const fetchVocabulary = async () => {
    try {
      setLoading(true);
      
      // Fetch known words
      const knownResponse = await fetch('/api/vocabulary/known', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      if (!knownResponse.ok) {
        throw new Error('Failed to fetch known words');
      }
      
      const knownData = await knownResponse.json();
      setKnownWords(knownData.words);
      
      // Fetch learning words
      const learningResponse = await fetch('/api/vocabulary/learning', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      if (!learningResponse.ok) {
        throw new Error('Failed to fetch learning words');
      }
      
      const learningData = await learningResponse.json();
      setLearningWords(learningData.words);
    } catch (err) {
      setError('Error fetching vocabulary: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWord = async (e) => {
    e.preventDefault();
    
    if (!newWord.trim()) {
      return;
    }
    
    try {
      const endpoint = activeTab === 'known' 
        ? '/api/vocabulary/known' 
        : '/api/vocabulary/learning';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ word: { word: newWord } })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add word to ${activeTab} list`);
      }
      
      // Refresh vocabulary lists
      fetchVocabulary();
      setNewWord('');
    } catch (err) {
      setError('Error adding word: ' + err.message);
      console.error(err);
    }
  };

  const handleMoveWord = async (word, targetList) => {
    try {
      // First, add to target list
      const addEndpoint = targetList === 'known'
        ? '/api/vocabulary/known'
        : '/api/vocabulary/learning';
      
      await fetch(addEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ word })
      });
      
      // Then, remove from current list
      const removeEndpoint = targetList === 'known'
        ? '/api/vocabulary/learning'
        : '/api/vocabulary/known';
      
      await fetch(removeEndpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ wordId: word.id })
      });
      
      // Refresh vocabulary lists
      fetchVocabulary();
    } catch (err) {
      setError('Error moving word: ' + err.message);
      console.error(err);
    }
  };

  const handleDeleteWord = async (wordId, list) => {
    try {
      const endpoint = list === 'known'
        ? '/api/vocabulary/known'
        : '/api/vocabulary/learning';
      
      await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ wordId })
      });
      
      // Refresh vocabulary lists
      fetchVocabulary();
    } catch (err) {
      setError('Error deleting word: ' + err.message);
      console.error(err);
    }
  };

  return (
    <div className="vocabulary-manager">
      <div className="card">
        <h1>Vocabulary Manager</h1>
        {error && <div style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>{error}</div>}
        
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'known' ? 'active' : ''}`}
            onClick={() => setActiveTab('known')}
          >
            Known Words ({knownWords.length})
          </button>
          <button 
            className={`tab ${activeTab === 'learning' ? 'active' : ''}`}
            onClick={() => setActiveTab('learning')}
          >
            Learning Words ({learningWords.length})
          </button>
        </div>
        
        <form onSubmit={handleAddWord} className="add-word-form">
          <div className="form-group" style={{ display: 'flex', gap: '1rem' }}>
            <input
              type="text"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder={`Add a new ${activeTab === 'known' ? 'known' : 'learning'} word...`}
              required
            />
            <button type="submit" className="btn">Add</button>
          </div>
        </form>
        
        {loading ? (
          <p>Loading vocabulary...</p>
        ) : (
          <div className="word-list">
            {activeTab === 'known' ? (
              knownWords.length > 0 ? (
                knownWords.map((word) => (
                  <div key={word.id} className="word-item">
                    <div className="word-text">
                      <span>{word.word}</span>
                      {word.translation && <span className="translation">{word.translation}</span>}
                    </div>
                    <div className="word-actions">
                      <button 
                        className="btn btn-small btn-secondary"
                        onClick={() => handleMoveWord(word, 'learning')}
                      >
                        Move to Learning
                      </button>
                      <button 
                        className="btn btn-small btn-danger"
                        onClick={() => handleDeleteWord(word.id, 'known')}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p>No known words yet. Add some words or process an article.</p>
              )
            ) : (
              learningWords.length > 0 ? (
                learningWords.map((word) => (
                  <div key={word.id} className="word-item">
                    <div className="word-text">
                      <span>{word.word}</span>
                      {word.translation && <span className="translation">{word.translation}</span>}
                    </div>
                    <div className="word-actions">
                      <button 
                        className="btn btn-small"
                        onClick={() => handleMoveWord(word, 'known')}
                      >
                        Move to Known
                      </button>
                      <button 
                        className="btn btn-small btn-danger"
                        onClick={() => handleDeleteWord(word.id, 'learning')}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p>No learning words yet. Add some words or process an article.</p>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VocabularyManager;
